import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, User, Building2, TrendingUp, ShieldCheck } from "lucide-react";
import { useContactModal } from "../context/ContactModalContext";

const CARD_ICONS = { User, Building2, TrendingUp, ShieldCheck };

const PARAGRAPH_CLASS =
  "mt-3 text-sm leading-relaxed text-gray-700 md:text-base";

const CTA_CARD_BG = "#121313";
const BTN_CLASS_DARK =
  "mt-4 inline-flex w-fit items-center gap-2 rounded-2xl border-2 border-white bg-transparent px-6 py-3 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-white hover:text-[#121313]";

/**
 * Renders popup or page content from service data.
 * sectionIdPrefix: "modal" (default) for modal; "service" for service pages. Contact form only in modal.
 */
export default function ServiceDetailContent({
  service,
  onClose,
  sectionIdPrefix = "modal",
}) {
  const id = (name) => `${sectionIdPrefix}-section-${name}`;
  const { openContactModal } = useContactModal() || {};
  const handleCtaClick = () => {
    if (sectionIdPrefix === "modal" && onClose) onClose();
    if (openContactModal) {
      openContactModal();
    } else {
      const el = document.getElementById(id("contact"));
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else if (sectionIdPrefix === "modal") {
        setTimeout(() => {
          const pageContact = document.getElementById("section-contact");
          if (pageContact) pageContact.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  };

  if (!service) return null;

  const { detail } = service;

  // Generic content when no detail
  if (!detail) {
    return (
      <div className="mt-6">
        <p className={PARAGRAPH_CLASS}>{service.desc}</p>
        <p className={PARAGRAPH_CLASS}>
          Unsere Spezialisten beraten Sie individuell zu dieser Dienstleistung –
          von der ersten Bedarfsklärung bis zur erfolgreichen Besetzung
          relevanter Positionen. Auf Wunsch erläutern wir unseren Prozess,
          Beispiele aus aktuellen Mandaten sowie typische Zeitpläne.
        </p>
        {service.to && (
          <div className="mt-6">
            <Link
              to={service.to}
              className="group btn btn-outline"
              onClick={onClose}
            >
              Mehr über diesen Service
              <ArrowRight className="h-5 w-6 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-0 w-full">
      {detail.sections?.map((section, i) => (
        <section
          key={i}
          id={id(String(i))}
          className="border-b border-gray-100 py-12 md:py-16 first:pt-8"
        >
          <div className="container-app mx-auto flex flex-col gap-8 px-4 md:flex-row md:gap-12 lg:gap-16">
            {section.leftLabel && (
              <div className="flex-shrink-0 md:w-36 lg:w-40">
                <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                  {section.leftLabel}
                </span>
              </div>
            )}
            <div className="min-w-0 flex-1 max-w-4xl">
              <h2 className="text-2xl font-semibold leading-snug text-gray-900 md:text-3xl">
                {section.heading}
              </h2>
              {section.cards && section.cards.length > 0 ? (
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {section.cards.map((card, j) => {
                    const IconComponent = CARD_ICONS[card.icon] || User;
                    return (
                      <div
                        key={j}
                        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
                      >
                        <div className="flex justify-center text-gray-600">
                          <IconComponent className="h-8 w-8" strokeWidth={1.5} />
                        </div>
                        <p className="mt-4 text-sm leading-relaxed text-gray-700 md:text-base">
                          {card.text}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : section.bullets && section.bullets.length > 0 ? (
                <ul className="mt-6 space-y-2 text-sm leading-relaxed text-gray-700 md:text-base list-disc list-outside pl-5">
                  {section.bullets.map((bullet, j) => (
                    <li key={j}>{bullet}</li>
                  ))}
                </ul>
              ) : (
                section.paragraphs?.map((p, j) => (
                  <p key={j} className={PARAGRAPH_CLASS}>
                    {p}
                  </p>
                ))
              )}
            </div>
          </div>
        </section>
      ))}

      {(detail.bottomImage || detail.bottomImages?.images?.length > 0 || detail.cta) ? (
        <div className="container-app mx-auto max-w-4xl px-4 py-12 md:py-16">
          {detail.bottomImage && (
            <section id={id("bottom-image")} className="pt-2">
              <h2 className="text-xl font-semibold text-gray-900 md:text-2xl">{detail.bottomImage.heading}</h2>
              <div className="mt-4 overflow-hidden rounded-2xl">
                <img
                  src={
                    typeof detail.bottomImage.image === "string"
                      ? detail.bottomImage.image
                      : detail.bottomImage.image?.default ?? detail.bottomImage.image
                  }
                  alt={detail.bottomImage.heading}
                  className=" w-full object-cover"
                />
              </div>
            </section>
          )}
          {detail.bottomImages?.images?.length > 0 && (
            <section id={id("bottom-images")} className="pt-8">
              {detail.bottomImages.sectionHeading && (
                <h2 className="text-xl font-semibold text-gray-900 md:text-2xl">
                  {detail.bottomImages.sectionHeading}
                </h2>
              )}
              <div className="mt-4 flex flex-col gap-4">
                {detail.bottomImages.images.map((item, idx) => {
                  const src =
                    typeof item.image === "string"
                      ? item.image
                      : item.image?.default ?? item.image;
                  const isSmall = item.size === "small";
                  return (
                    <div
                      key={idx}
                      className={
                        isSmall
                          ? "max-w-sm overflow-hidden rounded-2xl"
                          : "w-full overflow-hidden rounded-2xl"
                      }
                    >
                      <img
                        src={src}
                        alt={
                          item.alt ||
                          (detail.bottomImages.sectionHeading
                            ? `${detail.bottomImages.sectionHeading} - Bild ${idx + 1}`
                            : `Servicebild ${idx + 1}`)
                        }
                        className={
                          isSmall
                            ? "h-auto max-h-56 w-full object-cover"
                            : "h-auto w-full object-cover"
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {detail.cta && (
            <section id={id("cta")} className="border-b border-gray-100 py-10 md:py-12">
              <div className="flex flex-col gap-6 md:flex-row md:gap-10 lg:gap-12">
                <div className="flex-shrink-0 md:w-36 lg:w-40">
                  <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                    KONTAKT
                  </span>
                </div>
                <div className="min-w-0 flex-1 max-w-4xl">
                  <div
                    className="rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] md:p-8"
                    style={{ backgroundColor: CTA_CARD_BG }}
                  >
                    {detail.cta.heading && (
                      <h2
                        className={
                          detail.cta.smallerHeading
                            ? "text-lg font-semibold text-white md:text-xl"
                            : "text-xl font-semibold text-white md:text-2xl"
                        }
                      >
                        {detail.cta.heading}
                      </h2>
                    )}
                    <button
                      type="button"
                      onClick={handleCtaClick}
                      className={"group " + (detail.cta.heading ? BTN_CLASS_DARK : "mt-6 " + BTN_CLASS_DARK)}
                    >
                      {detail.cta.buttonText}
                      <ArrowRight className="h-5 w-6 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden />
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      ) : null}
    </div>
  );
}
